import type { LoginInput, Me, RegisterInput } from '@nexo/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, SESSION_EXPIRED_EVENT } from '../../lib/api';
import { keys } from '../../lib/queries';
import { setAlertDnd } from '../notifications/alerts';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: Status;
  user: Me | null;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: Me) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUserState] = useState<Me | null>(null);
  const [status, setStatus] = useState<Status>(() =>
    api.auth.isAuthenticated() ? 'loading' : 'anonymous',
  );

  const signIn = useCallback(
    (me: Me) => {
      // Another account's data must not leak into this one, but restoring the same session again
      // (twice in development's strict mode) must not wipe queries already in flight: their
      // pages would wait forever.
      if (queryClient.getQueryData<Me>(keys.me)?.id !== me.id) queryClient.clear();
      queryClient.setQueryData(keys.me, me);
      setUserState(me);
      setStatus('authenticated');
    },
    [queryClient],
  );

  const signOutLocally = useCallback(() => {
    queryClient.clear();
    setUserState(null);
    setStatus('anonymous');
  }, [queryClient]);

  // Restore the session on startup.
  useEffect(() => {
    if (!api.auth.isAuthenticated()) return;
    api.users.me().then(signIn, signOutLocally);
  }, [signIn, signOutLocally]);

  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, signOutLocally);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, signOutLocally);
  }, [signOutLocally]);

  // Sounds and system notifications follow the user's do-not-disturb settings.
  useEffect(() => {
    setAlertDnd({ dndUntil: user?.dndUntil ?? null, quietHours: user?.quietHours ?? null });
  }, [user?.dndUntil, user?.quietHours]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login: async (input) => signIn(await api.auth.login(input)),
      register: async (input) => signIn(await api.auth.register(input)),
      logout: async () => {
        await api.auth.logout();
        signOutLocally();
      },
      setUser: (me) => {
        setUserState(me);
        queryClient.setQueryData(keys.me, me);
      },
    }),
    [status, user, signIn, signOutLocally, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/** The authenticated user; only call below <RequireAuth>. */
export function useMe(): Me {
  const { user } = useAuth();
  if (!user) throw new Error('useMe called without an authenticated user');
  return user;
}
